import { ReactNode, useEffect, useRef, useState } from 'react';
import { useQueryClient } from 'react-query';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import CloudSyncIcon from '@mui/icons-material/CloudSync';
import { useDomainPresence } from 'src/Common/hooks/useDomainPresence';
import { SYSTEM_ALL_STATE_KEY } from 'src/Common/hooks/useSystemKillSwitch';
import { useBootOrchestrator } from '../../api/useOrchestratorBoot';
import {
  DownEvidence,
  NO_DOWN_EVIDENCE,
  downConfirmDelay,
  recordDownRead
} from './downConfirmation';

export type GatedDomain = 'opener' | 'unwinder';

/**
 * `replace` — the gated surface IS served by the dead pod (the opener's rules
 * table), so it is swapped for the boot panel.
 * `banner` — the surface is served by a DIFFERENT, unaffected service (the
 * unwinder's open-positions table comes from prediction-arbitrage-manager), so
 * the boot panel sits above it and the data stays on screen. Exposure is never
 * hidden for a control-plane reason.
 */
export type BootGateMode = 'replace' | 'banner';

const COPY: Record<
  GatedDomain,
  { down: string; banner: string; booting: string; stalled: string }
> = {
  opener: {
    down: 'El plano de control del opener está apagado — booteálo para configurar.',
    banner:
      'El plano de control del opener está apagado — no se pueden editar reglas hasta bootearlo.',
    booting:
      'Booteando el plano de control del opener — las reglas aparecen cuando el pod esté listo.',
    stalled:
      'El opener sigue sin estar listo. El boot puede haber fallado (pod sin arrancar) — reintentá.'
  },
  unwinder: {
    down: 'El plano de control del unwinder está apagado — booteálo para configurar.',
    banner:
      'El plano de control del unwinder está apagado — las posiciones de abajo son reales, pero no se pueden editar reglas hasta bootearlo.',
    booting:
      'Booteando el plano de control del unwinder — las reglas aparecen cuando el pod esté listo.',
    stalled:
      'El unwinder sigue sin estar listo. El boot puede haber fallado (pod sin arrancar) — reintentá.'
  }
};

/** How often the shared system-state query is re-read while a boot is pending. */
const BOOT_POLL_MS = 5000;

/**
 * How long a pending boot waits for `nodeReady` before the button re-enables.
 * A POST that 200s while the pod never becomes Ready (image pull failure, no
 * capacity) produces no error, so without a deadline the CTA would sit disabled
 * on "Booteando…" forever.
 */
export const BOOT_DEADLINE_MS = 180000;

/**
 * A DOWN that follows a known-up reading is debounced by the evidence rules in
 * `downConfirmation.ts` before the gate closes.
 *
 * A first observation with NO prior known-up reading is not debounced: there is
 * no in-progress work to protect on a fresh mount, and delaying the gate there
 * would just show a table whose writes are already dead.
 */

/**
 * Gates a rule-CRUD surface on its domain's `position-*-node` pod being up.
 *
 * From P5 the node pods scale to 0 with their domain, and rule CRUD is served
 * by THOSE pods — so with the domain off the screen would write into a dead
 * endpoint. Configuring means booting: this renders a boot CTA.
 *
 * Fails OPEN. The gate only closes when the backend positively reports
 * `nodeReady === false`; an older backend that reports no `domains` block at
 * all renders the children untouched.
 */
export const BootDomainGate = ({
  domain,
  mode = 'replace',
  children
}: {
  domain: GatedDomain;
  mode?: BootGateMode;
  children: ReactNode;
}) => {
  const { isNodeDown, dataUpdatedAt } = useDomainPresence();
  const qc = useQueryClient();
  const [bootRequested, setBootRequested] = useState(false);
  const [bootStalled, setBootStalled] = useState(false);
  const deadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const boot = useBootOrchestrator(domain);

  // What the backend says right now, before debouncing.
  const nodeDownReported = isNodeDown(domain);
  const [downConfirmed, setDownConfirmed] = useState(false);
  const seenUpRef = useRef(false);
  const evidenceRef = useRef<DownEvidence>(NO_DOWN_EVIDENCE);

  useEffect(() => {
    if (!nodeDownReported) {
      // A successful read that is not `false` is a known-up reading: from here
      // on, a flip to down is a TRANSITION and gets debounced.
      if (dataUpdatedAt > 0) seenUpRef.current = true;
      evidenceRef.current = NO_DOWN_EVIDENCE;
      setDownConfirmed(false);
      return;
    }

    if (!seenUpRef.current) {
      setDownConfirmed(true);
      return;
    }

    evidenceRef.current = recordDownRead(
      evidenceRef.current,
      dataUpdatedAt,
      Date.now()
    );

    if (downConfirmed) return;

    const delay = downConfirmDelay(evidenceRef.current, Date.now());
    if (delay <= 0) {
      setDownConfirmed(true);
      return;
    }
    // Always armed, so the wait is bounded even if no further read ever lands.
    const timer = setTimeout(() => setDownConfirmed(true), delay);
    return () => clearTimeout(timer);
  }, [nodeDownReported, dataUpdatedAt, downConfirmed]);

  const nodeDown = nodeDownReported && downConfirmed;
  const pending = (bootRequested || boot.isLoading) && !bootStalled;

  // The pod came up: drop the pending state even though the gate is about to
  // stop rendering. The component can stay mounted (banner mode) or be re-shown
  // by a later Shutdown, and a stale flag would paint a permanent "Booteando…".
  // The deadline timer goes with it — left armed it would fire invisibly and
  // leave `bootStalled` set, so a later deliberate Shutdown would open the panel
  // on "el boot puede haber fallado" with no boot in flight.
  useEffect(() => {
    if (!nodeDownReported) {
      if (deadlineRef.current) {
        clearTimeout(deadlineRef.current);
        deadlineRef.current = null;
      }
      setBootRequested(false);
      setBootStalled(false);
    }
  }, [nodeDownReported]);

  useEffect(() => {
    if (!pending || !nodeDownReported) return;
    const timer = setInterval(() => {
      void qc.invalidateQueries([SYSTEM_ALL_STATE_KEY]);
    }, BOOT_POLL_MS);
    return () => clearInterval(timer);
  }, [pending, nodeDownReported, qc]);

  useEffect(
    () => () => {
      if (deadlineRef.current) clearTimeout(deadlineRef.current);
    },
    []
  );

  const handleBoot = () => {
    setBootStalled(false);
    setBootRequested(true);
    if (deadlineRef.current) clearTimeout(deadlineRef.current);
    deadlineRef.current = setTimeout(() => {
      setBootStalled(true);
      setBootRequested(false);
    }, BOOT_DEADLINE_MS);
    boot.mutate(undefined, {
      onError: () => {
        if (deadlineRef.current) clearTimeout(deadlineRef.current);
        setBootRequested(false);
      }
    });
  };

  const message = pending
    ? COPY[domain].booting
    : bootStalled
      ? COPY[domain].stalled
      : mode === 'banner'
        ? COPY[domain].banner
        : COPY[domain].down;

  const panel = !nodeDown ? null : (
    <Box
      data-testid={`boot-domain-gate-${domain}`}
      sx={
        mode === 'banner'
          ? {
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              gap: 1.5,
              py: 0.75,
              px: 1.5,
              mb: 0.5,
              border: '1px solid',
              borderColor: 'warning.dark',
              borderRadius: 1
            }
          : { overflowY: 'auto', py: 2, px: 1 }
      }
    >
      <Typography
        sx={{
          color: bootStalled ? 'warning.main' : 'grey.500',
          fontSize: '0.85rem',
          mb: mode === 'banner' ? 0 : 2,
          flex: mode === 'banner' ? 1 : undefined
        }}
      >
        {message}
      </Typography>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          size="small"
          color="info"
          startIcon={
            pending ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <CloudSyncIcon />
            )
          }
          onClick={handleBoot}
          disabled={pending}
          sx={{ fontSize: '0.75rem', whiteSpace: 'nowrap' }}
        >
          {pending
            ? 'Booting…'
            : bootStalled
              ? 'Retry boot'
              : 'Boot control plane'}
        </Button>
      </Stack>
    </Box>
  );

  // The children keep slot 0 in BOTH states. Returning `<>{children}</>` when
  // open and `<>{panel}{children}</>` when closed would shift them to slot 1 and
  // make React delete + remount the whole subtree — on the unwinder that means
  // losing expanded rows, virtualizer scroll and in-progress edits on the
  // exposure screen. `null` holds the slot.
  if (mode === 'banner') {
    return (
      <>
        {panel}
        {children}
      </>
    );
  }
  return nodeDown ? panel : <>{children}</>;
};
